/**
 * New session — the facilitator's setup flow ("bonus section").
 *
 * Three settled things, each with a recommended default (C Mode rule 3):
 *   1. The question — what is being decided.
 *   2. The options — typed in quickly, or picked from workspace Items.
 *   3. The method — recommended by the one recommender (`recommendMethods`,
 *      the same advice a track's Run it live gives); adjusting is one tap.
 *      The list is the platform's method catalog (sessionAppActions): the
 *      recommended methods that fit first, then "More methods" in plain-word
 *      groups, each method that does not fit the option count saying why.
 *      A method that scores options against criteria (weighted scoring) also
 *      asks for its scale here, 1–5 recommended; the choice is saved in the
 *      session's tool settings, which every scoring screen reads.
 *
 * Creation goes through createSessionWithFeedback (the one session-creation path).
 * The invitation is the session link, shared from the host console.
 */

import { useEffect, useMemo, useState } from 'react';
import { uniqueLines } from '@/lib/uniqueLines';
import { useLocation, useNavigate } from 'react-router-dom';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { appTelemetry } from '@/lib/appTelemetry';
import { createSessionWithFeedback } from '@/lib/sessions/createSessionFlow';
import { SESSION_CREATE_LINE, useCreateRights } from '@/lib/authorization/createRights';
import { CREATE_SESSION_BUTTON_LABEL } from '@/lib/sessions/vocabulary';
import { useToast } from '@/components/ui/Toast';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, Eyebrow, PrimaryButton, SecondaryButton } from '../components/primitives';
import { GuideMomentCard } from '../components/flow';
import type { SessionPreset } from '@/lib/navigator/catalog';
import { normalizeSessionToolId, toolAsksForCriteria } from '@/lib/sessionToolCatalog';
import { DEFAULT_SCORING_SCALE, scoringScaleToolConfig, type ScoringScaleRange } from '@/lib/toolSettings';
import { ScoringScaleChoice } from '@/components/prioritization/session/ScoringScaleChoice';
import {
  SESSION_APP_MODE_CONFIG,
  sessionAppDefaultCriteriaLine,
  sessionAppMethod,
  sessionAppMethodSections,
  type SessionAppMethodChoice,
} from './sessionAppActions';

type OptionSource = 'typed' | 'items';

export default function NewSessionScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const app = getAppDefinition('session')!;
  const userId = useUserStore((s) => s.userId);
  const mayCreateSession = useCreateRights().session;
  const { showToast } = useToast();
  // Raw slice, not getItems() — that builds a fresh array per call and loops.
  const workspaceItems = useItemsStore((s) => s.items);
  // Opened straight from a link, nothing else has loaded the workspace's items
  // yet — "Pick from Items" would offer none.
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  // Start preset (PRD-start-intents §7): everything arrives pre-filled but
  // nothing is created until the person confirms — accept-or-edit, never silent.
  const preset =
    (location.state as { startPreset?: SessionPreset } | null)?.startPreset ?? null;
  const optionNoun = preset?.optionNoun ?? 'option';

  const [question, setQuestion] = useState(preset?.suggestedName ?? '');
  const [source, setSource] = useState<OptionSource>('typed');
  const [typedOptions, setTypedOptions] = useState(preset?.seedOptions?.join('\n') ?? '');
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [toolId, setToolId] = useState<string | null>(null);
  const [adjustingTool, setAdjustingTool] = useState(false);
  const [scoringScale, setScoringScale] = useState<ScoringScaleRange>(DEFAULT_SCORING_SCALE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeItems = useMemo(
    () => workspaceItems.filter((i) => !i.exit_status || i.exit_status === 'active'),
    [workspaceItems]
  );

  const optionCount =
    source === 'typed'
      ? uniqueLines(typedOptions.split('\n')).length
      : selectedItemIds.length;

  const methodSections = useMemo(() => sessionAppMethodSections(optionCount), [optionCount]);
  const recommended = methodSections.recommended;
  const effectiveTool = toolId ?? recommended.id;
  const effectiveMethod = sessionAppMethod(effectiveTool);
  const defaultCriteriaLine = sessionAppDefaultCriteriaLine(effectiveMethod);
  // A method that scores options against criteria scores them on a scale the
  // host picks here (the same rule the session wizard's Value Range step uses).
  const asksForScale = toolAsksForCriteria(effectiveMethod?.catalogId);

  const canCreate = question.trim().length > 0 && optionCount >= 2 && !submitting;

  const handleCreate = async () => {
    if (!userId) return;
    setSubmitting(true);
    setError(null);
    const options = uniqueLines(typedOptions.split('\n'));
    const result = await createSessionWithFeedback({
      name: question.trim(),
      facilitatorId: userId,
      createdBy: userId,
      intent: 'rank_priorities',
      sessionContextMode: source === 'typed' ? 'isolated' : 'app_linked',
      ...(source === 'typed' ? { isolatedItems: options } : { itemIds: selectedItemIds }),
      selectedToolId: effectiveTool,
      // A method that needs criteria before it can open (weighted scoring)
      // runs on its built-in set, declared on the session like any other.
      ...(effectiveMethod?.defaultCriteria?.length && {
        initialCriteriaIds: effectiveMethod.defaultCriteria.map((c) => c.id),
      }),
      ...(asksForScale && { toolConfig: scoringScaleToolConfig(scoringScale) }),
      // Its people answer in the product's live room (SESSION_APP_MODE_CONFIG).
      modeConfig: SESSION_APP_MODE_CONFIG,
      workspaceId: getCurrentWorkspaceId() ?? undefined,
    }, { toast: showToast });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'The session could not be created. Try again.');
      return;
    }
    // FR-20: guided setup produced its first real artifact.
    void appTelemetry.setupCompleted('session', {
      toolId: normalizeSessionToolId(effectiveTool),
      count: optionCount,
    });
    navigate(`/apps/session/${result.sessionId}/host`, { replace: true });
  };

  // Creating a session inserts into prioritization_sessions, whose insert rule
  // (migration 754) takes SESSION_RUN. Someone without it is not handed a form
  // the server will refuse at the end (build contract §2.8): the line instead.
  if (!mayCreateSession) {
    return (
      <AppShell app={app} backTo="/apps/session" backLabel="Back to your sessions">
        <ScreenHeading title="Set up a session" subtitle="Three things and you're live: the question, the options, the method." />
        <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]" data-testid="session-create-refused">
          {SESSION_CREATE_LINE}
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell app={app} backTo="/apps/session" backLabel="Back to your sessions">
      <ScreenHeading
        title="Set up a session"
        subtitle="Three things and you're live: the question, the options, the method."
      />
      <BallLine ballWith="you — the setup" />

      {preset ? (
        <p className="-mt-2 mb-4 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
          Prefilled from Start — everything here is editable.
        </p>
      ) : null}

      <div className="space-y-4">
        <AppCard>
          <Eyebrow>1 · The question</Eyebrow>
          <label className="mt-2 block font-body text-sm text-gray-700 dark:text-[var(--text-sec)]" htmlFor="session-question">
            What are you deciding?
          </label>
          <input
            id="session-question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. Which three initiatives do we fund this quarter?"
            className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
          />
        </AppCard>

        <AppCard>
          <Eyebrow>2 · The options</Eyebrow>
          <div className="mt-2 flex gap-2">
            <SecondaryButton
              onClick={() => setSource('typed')}
              className={source === 'typed' ? 'ring-2 ring-primary-600/40' : ''}
            >
              Type them in
            </SecondaryButton>
            <SecondaryButton
              onClick={() => setSource('items')}
              className={source === 'items' ? 'ring-2 ring-primary-600/40' : ''}
            >
              Pick from Items ({activeItems.length})
            </SecondaryButton>
          </div>
          {source === 'typed' ? (
            <>
              <textarea
                value={typedOptions}
                onChange={(e) => setTypedOptions(e.target.value)}
                rows={6}
                placeholder={`One ${optionNoun} per line, e.g.\nSelf-serve onboarding\nPartner API\nEU data residency`}
                className="mt-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
              />
              <p className="mt-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                {optionCount} {optionNoun}{optionCount === 1 ? '' : 's'} — you need at least 2.
              </p>
            </>
          ) : (
            <div className="mt-3 max-h-64 space-y-1 overflow-y-auto">
              {activeItems.length === 0 ? (
                <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
                  No active items in this workspace yet — type your options instead.
                </p>
              ) : (
                activeItems.map((item) => {
                  const checked = selectedItemIds.includes(item.id);
                  return (
                    <label
                      key={item.id}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)]"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedItemIds((prev) =>
                            checked ? prev.filter((id) => id !== item.id) : [...prev, item.id]
                          )
                        }
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-800 dark:text-[var(--text-pri)]">
                        {item.title}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          )}
        </AppCard>

        <div>
          <div className="mb-2">
            <Eyebrow>3 · The method</Eyebrow>
          </div>
          {!adjustingTool ? (
            <GuideMomentCard
              question="How should people weigh in?"
              recommendation={recommended.name}
              why={recommended.blurb}
              acceptLabel={canCreate ? 'Use it and create the session' : 'Use the recommendation'}
              onAccept={() => {
                setToolId(recommended.id);
                if (canCreate) void handleCreate();
              }}
              onAdjust={() => setAdjustingTool(true)}
              adjustLabel="See the methods"
            />
          ) : (
            <AppCard>
              <p className="px-2 font-body text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-[var(--text-ter)]">
                Recommended for {Math.max(optionCount, 2)} {optionNoun}s
              </p>
              <div className="mt-1 space-y-2">
                {methodSections.first.map((method) => (
                  <MethodOption
                    key={method.id}
                    method={method}
                    checked={effectiveTool === method.id}
                    onChoose={() => setToolId(method.id)}
                  />
                ))}
              </div>
              {methodSections.more.length > 0 ? (
                <div className="mt-4 border-t border-gray-200 pt-3 dark:border-[var(--rule)]">
                  <p className="font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                    More methods
                  </p>
                  {methodSections.more.map((section) => (
                    <div key={section.group} className="mt-3">
                      <p className="px-2 font-body text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-[var(--text-ter)]">
                        {section.label}
                      </p>
                      <div className="mt-1 space-y-1">
                        {section.methods.map((method) => (
                          <MethodOption
                            key={method.id}
                            method={method}
                            checked={effectiveTool === method.id}
                            onChoose={() => setToolId(method.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </AppCard>
          )}
          {defaultCriteriaLine ? (
            <p className="mt-2 font-body text-xs text-gray-600 dark:text-[var(--text-sec)]">
              {defaultCriteriaLine}
            </p>
          ) : null}
          {asksForScale ? (
            <AppCard className="mt-3">
              <ScoringScaleChoice
                legend="The scale"
                value={scoringScale}
                onChange={setScoringScale}
              />
            </AppCard>
          ) : null}
        </div>

        {error ? (
          <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">{error}</p>
        ) : null}

        <div className="flex justify-end">
          <PrimaryButton onClick={() => void handleCreate()} disabled={!canCreate}>
            {submitting ? 'Creating…' : CREATE_SESSION_BUTTON_LABEL}
          </PrimaryButton>
        </div>
      </div>
    </AppShell>
  );
}

function MethodOption({
  method,
  checked,
  onChoose,
}: {
  method: SessionAppMethodChoice;
  checked: boolean;
  onChoose: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)]">
      <input type="radio" name="tool" checked={checked} onChange={onChoose} className="mt-1 h-4 w-4" />
      <span className="min-w-0">
        <span className="block font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
          {method.name}
        </span>
        <span className="block font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">{method.blurb}</span>
        {method.fitNote ? (
          <span className="block font-body text-xs text-[var(--mustard)]">
            {method.fitNote.charAt(0).toUpperCase() + method.fitNote.slice(1)}.
          </span>
        ) : null}
      </span>
    </label>
  );
}
